import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { MessageCircle, X, Send, Loader2, Mail, Bot, User, Minimize2, Check, Trash2 } from "lucide-react";

interface EmailDraft {
  to: string;
  subject: string;
  body: string;
}

interface ChatAction {
  type: string;
  label: string;
  params: Record<string, any>;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  emailDraft?: EmailDraft;
  emailSent?: boolean;
  action?: ChatAction;
  actionDone?: { ok: boolean; message: string };
}

const GREETING: Message = {
  role: "assistant",
  content: `Hi! 👋 I'm your SaaSGama AI assistant. I can help with licenses, costs, renewals, and team management. I can also send emails — just ask!`,
};

// Render **bold** within a line as <strong>
function renderInline(text: string, keyPrefix: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>;
    }
    return <span key={`${keyPrefix}-${i}`}>{part}</span>;
  });
}

// Lightweight renderer: handles **bold**, bullet lists (-, *, •) and paragraphs.
function FormattedMessage({ content }: { content: string }) {
  const lines = content.split(/\n/);
  const blocks: JSX.Element[] = [];
  let bullets: string[] = [];
  let key = 0;

  const flushBullets = () => {
    if (bullets.length === 0) return;
    const items = bullets;
    blocks.push(
      <ul key={`ul-${key++}`} className="list-disc pl-4 space-y-0.5 my-1">
        {items.map((b, i) => (
          <li key={i}>{renderInline(b, `li-${key}-${i}`)}</li>
        ))}
      </ul>
    );
    bullets = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushBullets(); continue; }
    const bulletMatch = line.match(/^[-*•]\s+(.*)$/);
    if (bulletMatch) {
      bullets.push(bulletMatch[1]);
    } else {
      flushBullets();
      blocks.push(
        <p key={`p-${key++}`} className="my-1">{renderInline(line, `p-${key}`)}</p>
      );
    }
  }
  flushBullets();

  return <div className="leading-relaxed">{blocks}</div>;
}

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([GREETING]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [sendingTranscript, setSendingTranscript] = useState(false);
  const [sendingEmailIdx, setSendingEmailIdx] = useState<number | null>(null);
  const [runningActionIdx, setRunningActionIdx] = useState<number | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open && !minimized) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [messages, open, minimized]);

  // Load history when chat first opens
  useEffect(() => {
    if (open && !historyLoaded) {
      loadHistory();
    }
  }, [open]);

  const loadHistory = async () => {
    try {
      const res = await fetch("/api/chat/history", { credentials: "include" });
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages);
      }
    } catch {
      // keep default greeting on error
    } finally {
      setHistoryLoaded(true);
    }
  };

  const saveHistory = useCallback(async (msgs: Message[]) => {
    try {
      await fetch("/api/chat/history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ messages: msgs }),
      });
    } catch { /* non-fatal */ }
  }, []);

  const clearChat = async () => {
    setClearing(true);
    try {
      await fetch("/api/chat/history", { method: "DELETE", credentials: "include" });
      setMessages([GREETING]);
      setHistoryLoaded(true);
    } catch {
      toast({ title: "Failed to clear chat", variant: "destructive" });
    } finally {
      setClearing(false);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: Message = { role: "user", content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch("/api/chat/message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: text,
          history: updated.slice(-10).map(({ role, content, action, actionDone }) => {
            let fullContent = content;
            if (action) {
              fullContent += `\n[Action proposed: ${action.label}]`;
              if (actionDone) {
                fullContent += `\n[Result: ${actionDone.ok ? 'Done' : 'Failed — ' + actionDone.message}]`;
              } else {
                fullContent += `\n[Awaiting user confirmation — do not re-propose this action]`;
              }
            }
            return { role, content: fullContent };
          }),
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to get response");

      const withReply: Message[] = [...updated, { role: "assistant", content: data.reply, emailDraft: data.emailDraft, action: data.action }];
      setMessages(withReply);
      saveHistory(withReply);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (idx: number, action: ChatAction) => {
    setRunningActionIdx(idx);
    try {
      const res = await fetch("/api/chat/execute-action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ type: action.type, params: action.params }),
      });
      const data = await res.json();
      const ok = !!data.ok;
      const resultMsg = data.message || (ok ? "Done." : "Could not complete the action.");
      setMessages((prev) => {
        const updated = prev.map((m, i) => (i === idx ? { ...m, actionDone: { ok, message: resultMsg } } : m));
        if (!ok) updated.push({ role: "assistant", content: resultMsg });
        saveHistory(updated);
        return updated;
      });
      toast({ title: ok ? "Done" : "Action not completed", description: data.message, variant: ok ? undefined : "destructive" });
    } catch (err: any) {
      toast({ title: "Failed", description: err.message, variant: "destructive" });
    } finally {
      setRunningActionIdx(null);
    }
  };

  const sendEmail = async (idx: number, draft: EmailDraft) => {
    setSendingEmailIdx(idx);
    try {
      const res = await fetch("/api/chat/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to send");
      }
      setMessages((prev) => {
        const updated = prev.map((m, i) => (i === idx ? { ...m, emailSent: true } : m));
        saveHistory(updated);
        return updated;
      });
      toast({ title: "Email sent", description: data.message });
    } catch (err: any) {
      toast({ title: "Failed to send email", description: err.message, variant: "destructive" });
    } finally {
      setSendingEmailIdx(null);
    }
  };

  const sendTranscript = async () => {
    if (messages.length <= 1) return;
    setSendingTranscript(true);
    try {
      const res = await fetch("/api/chat/send-transcript", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ history: messages.map(({ role, content }) => ({ role, content })) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast({ title: "Transcript sent", description: data.message });
    } catch (err: any) {
      toast({ title: "Failed to send transcript", description: err.message, variant: "destructive" });
    } finally {
      setSendingTranscript(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setMinimized(false);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 sm:bottom-6 right-4 z-[9999] w-14 h-14 sm:w-12 sm:h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
          aria-label="Open AI Chat"
        >
          <MessageCircle className="h-7 w-7 sm:h-6 sm:w-6" />
        </button>
      )}

      {open && (
        <div
          className={`fixed bottom-20 sm:bottom-6 right-4 z-[9999] w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col transition-all duration-200 ${
            minimized ? "h-14" : "h-[85vh] sm:h-[600px] max-h-[calc(100vh-6rem)] sm:max-h-[calc(100vh-2rem)]"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-blue-600 rounded-t-2xl text-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="h-4 w-4" />
              </div>
              <div>
                <p className="font-semibold text-sm">SaaSGama AI</p>
                <p className="text-xs text-blue-100">Online</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!minimized && messages.length > 1 && (
                <>
                  <button onClick={sendTranscript} disabled={sendingTranscript} className="p-1.5 hover:bg-white/20 rounded-full" title="Email transcript">
                    {sendingTranscript ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  </button>
                  <button onClick={clearChat} disabled={clearing} className="p-1.5 hover:bg-white/20 rounded-full" title="Clear chat">
                    {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </>
              )}
              <button onClick={() => setMinimized(!minimized)} className="p-1.5 hover:bg-white/20 rounded-full" title={minimized ? "Expand" : "Minimize"}>
                <Minimize2 className="h-4 w-4" />
              </button>
              <button onClick={handleClose} className="p-1.5 hover:bg-white/20 rounded-full" title="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!minimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.map((msg, i) => (
                  <div key={i}>
                    <div className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
                      <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${msg.role === "user" ? "bg-blue-100" : "bg-green-100"}`}>
                        {msg.role === "user" ? <User className="h-3.5 w-3.5 text-blue-600" /> : <Bot className="h-3.5 w-3.5 text-green-600" />}
                      </div>
                      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${msg.role === "user" ? "bg-blue-600 text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"}`}>
                        {msg.role === "assistant" ? <FormattedMessage content={msg.content} /> : msg.content}
                      </div>
                    </div>

                    {/* Action confirmation card */}
                    {msg.action && (
                      <div className="ml-9 mt-2 border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
                        {msg.actionDone ? (
                          <div className={`flex items-center gap-2 text-sm font-medium ${msg.actionDone.ok ? "text-green-600" : "text-red-600"}`}>
                            <Check className="h-4 w-4" /> {msg.actionDone.message}
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-slate-700">
                              {renderInline(msg.action.label, `act-${i}`)}
                            </p>
                            <Button
                              size="sm"
                              onClick={() => runAction(i, msg.action!)}
                              disabled={runningActionIdx === i}
                              className="w-full h-8 bg-blue-600 hover:bg-blue-700 text-xs"
                            >
                              {runningActionIdx === i ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                              Yes, do it
                            </Button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Email draft card */}
                    {msg.emailDraft && (
                      <div className="ml-9 mt-2 border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
                        {msg.emailSent ? (
                          <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                            <Check className="h-4 w-4" /> Email sent to {msg.emailDraft.to}
                          </div>
                        ) : (
                          <>
                            <div className="text-xs text-slate-600 space-y-1">
                              <p><span className="text-slate-400">To:</span> {msg.emailDraft.to}</p>
                              <p><span className="text-slate-400">Subject:</span> {msg.emailDraft.subject}</p>
                              <p className="text-slate-500 whitespace-pre-line line-clamp-4 border-t border-slate-200 pt-1 mt-1">
                                {msg.emailDraft.body}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => sendEmail(i, msg.emailDraft!)}
                                disabled={sendingEmailIdx === i}
                                className="flex-1 h-8 bg-blue-600 hover:bg-blue-700 text-xs"
                              >
                                {sendingEmailIdx === i ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                                Yes, send it
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-3.5 w-3.5 text-green-600" />
                    </div>
                    <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-3 py-2">
                      <div className="flex gap-1 items-center h-5">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0ms]" />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:150ms]" />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:300ms]" />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-slate-100 flex-shrink-0">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    placeholder="Ask anything or send an email..."
                    disabled={loading}
                    className="text-sm flex-1 h-9 rounded-full border-slate-200 focus:border-blue-400"
                  />
                  <Button size="sm" onClick={sendMessage} disabled={!input.trim() || loading} className="h-9 w-9 p-0 rounded-full bg-blue-600 hover:bg-blue-700 flex-shrink-0">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
